import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Activity, BarChart3, LineChart, TrendingUp, TrendingDown, UserCheck, CheckCircle, XCircle } from "lucide-react";
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

export default function TeamReportsSection({
  // Alerts
  performanceAlerts,
  showAlerts,
  setShowAlerts,
  // Filters
  filterOptions,
  selectedPeriod,
  setSelectedPeriod,
  selectedTeam,
  setSelectedTeam,
  teams,
  selectedPlayers,
  setSelectedPlayers,
  selectedSport,
  setSelectedSport,
  selectedSchool,
  setSelectedSchool,
  selectedGender,
  setSelectedGender,
  selectedLevel,
  setSelectedLevel,
  customDateRange,
  setCustomDateRange,
  // Trends
  showTrendCharts,
  setShowTrendCharts,
  teamTrends,
  comparisonMode,
  setComparisonMode,
  selectedComparisonPlayers,
  setSelectedComparisonPlayers,
  // Reports
  teamReports,
  // Actions
  exportToCSV,
  printReport
}) {
  return (
    <div className="space-y-6" data-testid="team-reports-section">
      {/* Performance Alerts */}
      {performanceAlerts && performanceAlerts.total_alerts > 0 && (
        <Card className="border-2 border-orange-200 bg-orange-50 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-orange-900 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Performance Alerts
              <span className="ml-2 px-2 py-1 text-xs bg-orange-200 text-orange-800 rounded-full">
                {performanceAlerts.total_alerts} alerts
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {performanceAlerts.alerts.slice(0, showAlerts ? undefined : 4).map((alert, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border-2 ${
                    alert.severity === 'high' 
                      ? 'bg-red-50 border-red-300' 
                      : 'bg-yellow-50 border-yellow-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{alert.player_name}</p>
                      <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      alert.severity === 'high' 
                        ? 'bg-red-200 text-red-800' 
                        : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      {alert.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {performanceAlerts.alerts.length > 4 && (
              <Button
                onClick={() => setShowAlerts(!showAlerts)}
                variant="outline"
                className="mt-4"
              >
                {showAlerts ? 'Show Less' : `Show All ${performanceAlerts.total_alerts} Alerts`}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Enhanced Filters Card */}
      <Card className="border-2 border-purple-200 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-purple-900 flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                Team Performance Reports
              </CardTitle>
              <CardDescription>View mental performance metrics for your team members</CardDescription>
            </div>
            {/* Export Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={exportToCSV}
                variant="outline"
                size="sm"
                disabled={!teamReports || !teamReports.players || teamReports.players.length === 0}
                data-testid="export-csv-btn"
              >
                📥 Export CSV
              </Button>
              <Button
                onClick={printReport}
                variant="outline"
                size="sm"
                disabled={!teamReports || !teamReports.players || teamReports.players.length === 0}
                data-testid="print-report-btn"
              >
                🖨️ Print
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Row 1: Period, Team, Players */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>Time Period</Label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
                data-testid="period-select"
              >
                <option value="weekly">Last 7 Days</option>
                <option value="monthly">Last 30 Days</option>
                <option value="quarterly">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            
            <div>
              <Label>Team</Label>
              <select
                value={selectedTeam || ''}
                onChange={(e) => setSelectedTeam(e.target.value || null)}
                className="w-full mt-1 p-2 border rounded-md"
                data-testid="team-select"
              >
                <option value="">All Teams</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>

            {/* Player Multi-Select */}
            <div>
              <Label>Players {selectedPlayers.length > 0 && `(${selectedPlayers.length} selected)`}</Label>
              <select
                multiple
                value={selectedPlayers}
                onChange={(e) => setSelectedPlayers(Array.from(e.target.selectedOptions, opt => opt.value))}
                className="w-full mt-1 p-2 border rounded-md h-20"
                data-testid="players-select"
              >
                {filterOptions?.players?.map(player => (
                  <option key={player.id} value={player.id}>{player.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Advanced Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label>Sport</Label>
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
                data-testid="sport-select"
              >
                <option value="">All Sports</option>
                {filterOptions?.sports?.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>School</Label>
              <select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
                data-testid="school-select"
              >
                <option value="">All Schools</option>
                {filterOptions?.schools?.map(school => (
                  <option key={school} value={school}>{school}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Gender</Label>
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
                data-testid="gender-select"
              >
                <option value="">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label>Level</Label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
                data-testid="level-select"
              >
                <option value="">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="elite">Elite</option>
              </select>
            </div>
          </div>

          {/* Custom Date Range */}
          {selectedPeriod === 'custom' && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Start Date</Label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-md"
                  data-testid="end-date-input"
                />
              </div>
            </div>
          )}

          {/* Trend Chart Toggle */}
          <div className="flex gap-4 mt-4">
            <Button
              onClick={() => setShowTrendCharts(!showTrendCharts)}
              variant={showTrendCharts ? "default" : "outline"}
              size="sm"
              data-testid="toggle-trends-btn"
            >
              <LineChart className="w-4 h-4 mr-2" />
              {showTrendCharts ? 'Hide Trends' : 'Show Trend Charts'}
            </Button>
            <Button
              onClick={() => setComparisonMode(!comparisonMode)}
              variant={comparisonMode ? "default" : "outline"}
              size="sm"
              data-testid="toggle-comparison-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {comparisonMode ? 'Exit Comparison' : 'Compare Players'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Team Trend Charts */}
      {showTrendCharts && teamTrends && teamTrends.daily_trends && (
        <Card className="border-2 border-blue-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-blue-900 flex items-center gap-2">
              <LineChart className="w-5 h-5" />
              Team Performance Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={teamTrends.daily_trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis domain={[0, 10]} />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Legend />
                <Area type="monotone" dataKey="avg_mood" stroke="#3B82F6" fill="#93C5FD" name="Mood" />
                <Area type="monotone" dataKey="avg_stress" stroke="#EF4444" fill="#FCA5A5" name="Stress" />
                <Area type="monotone" dataKey="avg_focus" stroke="#10B981" fill="#6EE7B7" name="Focus" />
                <Area type="monotone" dataKey="avg_confidence" stroke="#8B5CF6" fill="#C4B5FD" name="Confidence" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Player Comparison Mode */}
      {comparisonMode && filterOptions?.players && (
        <Card className="border-2 border-indigo-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-indigo-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Player Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Label>Select Players to Compare (max 4)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {filterOptions.players.map(player => (
                  <Button
                    key={player.id}
                    size="sm"
                    variant={selectedComparisonPlayers.includes(player.id) ? "default" : "outline"}
                    onClick={() => {
                      if (selectedComparisonPlayers.includes(player.id)) {
                        setSelectedComparisonPlayers(prev => prev.filter(id => id !== player.id));
                      } else if (selectedComparisonPlayers.length < 4) {
                        setSelectedComparisonPlayers(prev => [...prev, player.id]);
                      }
                    }}
                    data-testid={`compare-player-${player.id}`}
                  >
                    {player.name}
                  </Button>
                ))}
              </div>
            </div>

            {selectedComparisonPlayers.length > 0 && teamReports?.players && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {teamReports.players
                  .filter(p => selectedComparisonPlayers.includes(p.player_id))
                  .map(player => (
                    <Card key={player.player_id} className="bg-indigo-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">{player.player_name}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p>Mood: <span className="font-bold">{player.metrics.average_mood}</span></p>
                        <p>Stress: <span className="font-bold">{player.metrics.average_stress}</span></p>
                        <p>Focus: <span className="font-bold">{player.metrics.average_focus}</span></p>
                        <p>Confidence: <span className="font-bold">{player.metrics.average_confidence}</span></p>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Team Averages Summary */}
      {teamReports && teamReports.team_averages && (
        <Card className="border-2 border-green-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-green-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Team Averages ({teamReports.period})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Avg Mood</p>
                <p className="text-2xl font-bold text-blue-600">{teamReports.team_averages.average_mood}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Avg Stress</p>
                <p className="text-2xl font-bold text-red-600">{teamReports.team_averages.average_stress}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Avg Focus</p>
                <p className="text-2xl font-bold text-green-600">{teamReports.team_averages.average_focus}</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Avg Confidence</p>
                <p className="text-2xl font-bold text-purple-600">{teamReports.team_averages.average_confidence}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Player Cards */}
      {teamReports && teamReports.players && teamReports.players.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teamReports.players.map((player, idx) => (
            <Card key={idx} className="border shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{player.player_name}</CardTitle>
                    <CardDescription>{player.team_name || 'No Team'}</CardDescription>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    player.metrics.adherence_rate >= 80 
                      ? 'bg-green-100 text-green-800'
                      : player.metrics.adherence_rate >= 50
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                  }`}>
                    {player.metrics.adherence_rate}% adherence
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-500">😊</span>
                    <span>Mood: <strong>{player.metrics.average_mood}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">😰</span>
                    <span>Stress: <strong>{player.metrics.average_stress}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">🎯</span>
                    <span>Focus: <strong>{player.metrics.average_focus}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-500">💪</span>
                    <span>Confidence: <strong>{player.metrics.average_confidence}</strong></span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    {player.metrics.sessions_completed} sessions
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-blue-500" />
                    {player.metrics.active_goals} active goals
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : teamReports && (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-gray-500">No player data available for the selected filters.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
